/*
 * BrandingSettingsPage — Tenant admin's homepage customization dashboard.
 *
 * Route: /portal/settings/branding
 *
 * Sections:
 *   1. Template picker — Modern / Classic / Bold
 *   2. Logo upload — drag & drop or click
 *   3. Brand colors — primary / accent / highlight (color pickers)
 *   4. Hero copy — headline, subhead, CTAs
 *   5. Features (add / remove / edit)
 *   6. Testimonials (add / remove / edit)
 *   7. Contact info
 *   8. Publish toggle + preview link
 */
import React, { useEffect, useRef, useState } from 'react';
import { api } from './api';
import { errorMessage } from './errors';
import { toast } from '../hooks/use-toast';
import { brandAssetUrl } from '../branding';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TenantSiteRenderer, MAX_FEATURES, ICON_OPTIONS } from '../TenantSitePage';
import {
  Save, Upload, Eye, Globe, Palette, Sparkles, Building2,
  Plus, Trash2, ExternalLink, Check, X, Image as ImageIcon,
  Monitor, Smartphone, RefreshCw,
 MapPin } from 'lucide-react';
import { US_STATES } from './shared/StateSelect';
import { Input } from '../components/ui/input';
import PhoneInput from './shared/PhoneInput';

const TEMPLATES = [
  { key: 'modern',  name: 'Modern',  desc: 'Light, spacious, serif — fits most brands.' },
  { key: 'classic', name: 'Classic', desc: 'Centered, warm, traditional feel.' },
  { key: 'bold',    name: 'Bold',    desc: 'Dark hero, vibrant accents — modern SaaS.' },
];

const PALETTE_PRESETS = [
  { name: 'Navy · Green',    primary: '#070B5F', accent: '#6BA82E', highlight: '#A6E56C' },
  { name: 'Charcoal · Gold', primary: '#0F172A', accent: '#D4AF37', highlight: '#FCE38A' },
  { name: 'Wine · Cream',    primary: '#5C1A1B', accent: '#C9A96E', highlight: '#F5E1DA' },
  { name: 'Forest · Sky',    primary: '#14532D', accent: '#38BDF8', highlight: '#BAE6FD' },
  { name: 'Indigo · Coral',  primary: '#312E81', accent: '#F97316', highlight: '#FED7AA' },
  { name: 'Slate · Emerald', primary: '#1E293B', accent: '#10B981', highlight: '#6EE7B7' },
];


export default function BrandingSettingsPage() {
  const [tenant, setTenant] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const iconRef = useRef(null);
  const fileRef = useRef(null);
  const coverRef = useRef(null);

  const [loadError, setLoadError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [leadForms, setLeadForms] = useState([]);

  useEffect(() => {
    setLoadError(null);
    api.get('/tenants/mine')
      .then(({ data }) => setTenant(data))
      .catch((e) => setLoadError(errorMessage(e, 'Could not load your brand settings')));
  }, [attempt]);

  // The forms the site's contact form can post to; a failed lookup leaves the picker empty.
  useEffect(() => {
    api.get('/v2/leadgen/forms').then(({ data }) => setLeadForms(Array.isArray(data?.items) ? data.items : [])).catch(() => {});
  }, []);

  if (loadError) {
    return (
      <div className="p-6" data-testid="branding-load-error">
        <div className="text-red-600 font-semibold">{loadError}</div>
        <button onClick={() => setAttempt((n) => n + 1)} data-testid="branding-retry"
                className="mt-3 px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-semibold hover:opacity-90">
          Try again
        </button>
      </div>
    );
  }
  if (!tenant) return <div className="p-6 text-slate-400">Loading brand settings…</div>;

  const update = (path, value) => {
    setTenant((prev) => {
      const t = { ...prev };
      const parts = path.split('.');
      let cur = t;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return t;
    });
  };

  const save = async () => {
    if (['primary', 'accent', 'highlight'].some((k) => !HEX_COLOR.test(tenant.colors?.[k] || ''))) {
      toast({ title: 'Check your colors', description: 'Each brand color needs a value like #1A2B3C.' });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch('/tenants/mine', {
        name: tenant.name,
        template: tenant.template,
        logo_url: tenant.logo_url,
        favicon_url: tenant.favicon_url,
        cover_photo_url: tenant.cover_photo_url,
        colors: tenant.colors,
        content: tenant.content,
        published: tenant.published,
        licensed_states: tenant.licensed_states || [],
      });
      setTenant(data);
      toast({ title: 'Saved', description: 'Brand settings updated.' });
    } catch (e) {
      toast({ title: 'Save failed', description: errorMessage(e, 'Try again') });
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async (kind, file) => {
    const icon = kind === 'favicon';
    (icon ? setUploadingIcon : setUploading)(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/tenants/mine/${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setTenant((t) => ({ ...t, [`${kind}_url`]: data[`${kind}_url`] }));
      toast({ title: icon ? 'Favicon uploaded' : 'Logo uploaded' });
    } catch (e) {
      toast({ title: 'Upload failed', description: errorMessage(e, 'Try a smaller file') });
    } finally {
      (icon ? setUploadingIcon : setUploading)(false);
    }
  };
  const uploadLogo = (file) => uploadImage('logo', file);

  const uploadCover = async (file) => {
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/tenants/mine/cover-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setTenant((t) => ({ ...t, cover_photo_url: data.cover_photo_url }));
      toast({ title: 'Cover photo uploaded' });
    } catch (e) {
      toast({ title: 'Upload failed', description: errorMessage(e, 'Try a smaller image (max 10 MB)') });
    } finally {
      setUploadingCover(false);
    }
  };

  const publicUrl = `/site/${tenant.slug}`;
  const previewUrl = `${publicUrl}?preview=1`;

  return (
    <div className="p-6 max-w-[1700px] mx-auto" data-testid="branding-settings-page">
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_680px] gap-6">
        <div className="space-y-6 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: '#6BA82E' }}>Brand Settings</div>
              <h1 className="font-serif text-3xl font-bold" style={{ color: '#070B5F' }}>Customize your homepage</h1>
              <div className="text-sm text-slate-500 mt-1">Public URL: <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="text-[#6BA82E] font-mono underline">{publicUrl}</a></div>
            </div>
            <div className="flex items-center gap-2">
              <a href={previewUrl} target="_blank" rel="noopener noreferrer"
                 className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-50 inline-flex items-center gap-2"
                 data-testid="preview-site-btn">
                <ExternalLink size={14} /> Open full preview
              </a>
              <button onClick={save} disabled={busy} data-testid="save-brand-btn"
                      className="px-4 py-2 rounded-lg bg-[#070B5F] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2">
                <Save size={14} /> {busy ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>

      {/* Publish */}
      <div className="rounded-xl border p-4 flex items-center justify-between"
           style={{ background: tenant.published ? '#ECFDF5' : '#FEF3C7', borderColor: tenant.published ? '#A7F3D0' : '#FCD34D' }}>
        <div className="flex items-center gap-3">
          <Globe size={18} style={{ color: tenant.published ? '#059669' : '#B45309' }} />
          <div>
            <div className="font-semibold text-sm" style={{ color: tenant.published ? '#065F46' : '#78350F' }}>
              {tenant.published ? 'Site is live' : 'Site is unpublished'}
            </div>
            <div className="text-xs" style={{ color: tenant.published ? '#065F46' : '#78350F' }}>
              {tenant.published ? 'Anyone with the URL can view your public site.' : 'Publish to make your site discoverable.'}
            </div>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="publish-toggle">
          <input type="checkbox" checked={tenant.published} onChange={(e) => update('published', e.target.checked)} className="w-4 h-4" />
          <span className="text-sm font-semibold" style={{ color: tenant.published ? '#065F46' : '#78350F' }}>
            {tenant.published ? 'Published' : 'Draft'}
          </span>
        </label>
      </div>

      {/* Licensed states (ticket 77): drives the fee worksheet state picker and the RE agent license default */}
      <Section title="Licensed states" icon={MapPin}>
        <p className="text-xs text-slate-500 mb-3">States this brokerage is licensed in. The Fee Worksheet only offers these; leave empty to allow any state.</p>
        <div className="flex flex-wrap gap-1.5" data-testid="licensed-states">
          {US_STATES.map(([code]) => {
            const on = (tenant.licensed_states || []).includes(code);
            return (
              <button key={code} type="button" data-testid={`licensed-state-${code}`} aria-pressed={on}
                onClick={() => update('licensed_states', on ? (tenant.licensed_states || []).filter((c) => c !== code) : [...(tenant.licensed_states || []), code])}
                className={`text-[11px] font-semibold px-2 py-1 rounded-md border transition ${on ? 'text-white' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                style={on ? { background: '#070B5F', borderColor: '#070B5F' } : undefined}>
                {code}
              </button>
            );
          })}
        </div>
      </Section>

      {/* Template picker */}
      <Section title="Template" icon={Sparkles}>
        <div className="grid md:grid-cols-3 gap-3">
          {TEMPLATES.map((t) => {
            const active = tenant.template === t.key;
            return (
              <button key={t.key} onClick={() => update('template', t.key)} data-testid={`template-${t.key}`}
                      className="p-4 rounded-xl border-2 text-left transition"
                      style={active ? { borderColor: '#070B5F', background: '#F1F5F9' } : { borderColor: '#e2e8f0', background: '#fff' }}>
                <div className="flex items-center justify-between mb-1">
                  <div className="font-semibold" style={{ color: '#070B5F' }}>{t.name}</div>
                  {active && <Check size={16} style={{ color: '#6BA82E' }} />}
                </div>
                <div className="text-xs text-slate-500">{t.desc}</div>
              </button>
            );
          })}
        </div>
      </Section>

      {/* Logo */}
      <Section title="Logo" icon={ImageIcon}>
        <div className="flex items-center gap-4">
          <div className="w-40 h-24 rounded-xl border-2 border-dashed flex items-center justify-center bg-slate-50"
               style={{ borderColor: '#cbd5e1' }}>
            {tenant.logo_url
              ? <img src={brandAssetUrl(tenant.logo_url)} alt="logo" className="max-w-full max-h-full object-contain" />
              : <ImageIcon size={28} className="text-slate-300" />}
          </div>
          <div>
            <input type="file" accept="image/*" ref={fileRef} className="hidden"
                   onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0])} data-testid="logo-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid="upload-logo-btn">
              <Upload size={14} /> {uploading ? 'Uploading…' : (tenant.logo_url ? 'Replace logo' : 'Upload logo')}
            </button>
            {tenant.logo_url && (
              <button onClick={() => update('logo_url', null)}
                      className="ml-2 px-3 py-2 rounded-lg text-red-600 text-sm font-semibold hover:bg-red-50">
                <Trash2 size={13} className="inline" /> Remove
              </button>
            )}
            <div className="text-xs text-slate-500 mt-2">PNG, JPG, GIF, WebP or SVG. Max 5 MB. Transparent background recommended.</div>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-5 pt-5 border-t border-slate-100" data-testid="favicon-field">
          <div className="w-12 h-12 rounded-lg border-2 border-dashed flex items-center justify-center bg-slate-50"
               style={{ borderColor: '#cbd5e1' }}>
            {tenant.favicon_url
              ? <img src={brandAssetUrl(tenant.favicon_url)} alt="favicon" className="w-8 h-8 object-contain" data-testid="favicon-preview" />
              : <ImageIcon size={18} className="text-slate-300" />}
          </div>
          <div>
            <input type="file" accept="image/png,image/x-icon,image/svg+xml,image/jpeg,.ico" ref={iconRef} className="hidden"
                   onChange={(e) => e.target.files[0] && uploadImage('favicon', e.target.files[0])} data-testid="favicon-file-input" />
            <button onClick={() => iconRef.current?.click()} disabled={uploadingIcon}
                    className="px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid="upload-favicon-btn">
              <Upload size={14} /> {uploadingIcon ? 'Uploading…' : (tenant.favicon_url ? 'Replace favicon' : 'Upload favicon')}
            </button>
            {tenant.favicon_url && (
              <button onClick={() => update('favicon_url', null)}
                      className="ml-2 px-3 py-2 rounded-lg text-red-600 text-sm font-semibold hover:bg-red-50"
                      data-testid="remove-favicon-btn">
                <Trash2 size={13} className="inline" /> Remove
              </button>
            )}
            <div className="text-xs text-slate-500 mt-2">The small icon in the browser tab. Square PNG, ICO or SVG. Max 1 MB.</div>
          </div>
        </div>
      </Section>

      {/* Cover Photo — hero banner on the tenant's Dashboard */}
      <Section title="Cover Photo" icon={ImageIcon}>
        <div className="flex flex-col md:flex-row gap-4">
          <div
            className="w-full md:w-80 h-40 rounded-xl border-2 border-dashed flex items-center justify-center bg-slate-50 overflow-hidden relative"
            style={{ borderColor: '#cbd5e1' }}
            data-testid="cover-photo-preview"
          >
            {tenant.cover_photo_url ? (
              <>
                <img src={brandAssetUrl(tenant.cover_photo_url)} alt="cover" className="w-full h-full object-cover" />
                {/* subtle overlay so a preview title reads well — matches how it looks on the Dashboard */}
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.5) 100%)' }} />
                <div className="absolute bottom-2 left-3 text-white text-[11px] font-serif">Dashboard preview</div>
              </>
            ) : (
              <div className="text-center text-slate-400 text-xs">
                <ImageIcon size={28} className="mx-auto mb-1 text-slate-300" />
                <div>1600×400 recommended</div>
              </div>
            )}
          </div>
          <div>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              ref={coverRef}
              className="hidden"
              onChange={(e) => e.target.files[0] && uploadCover(e.target.files[0])}
              data-testid="cover-file-input"
            />
            <button
              onClick={() => coverRef.current?.click()}
              disabled={uploadingCover}
              className="px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="upload-cover-btn"
            >
              <Upload size={14} /> {uploadingCover ? 'Uploading…' : (tenant.cover_photo_url ? 'Replace cover' : 'Upload cover photo')}
            </button>
            {tenant.cover_photo_url && (
              <button
                onClick={() => update('cover_photo_url', null)}
                data-testid="remove-cover-btn"
                className="ml-2 px-3 py-2 rounded-lg text-red-600 text-sm font-semibold hover:bg-red-50"
              >
                <Trash2 size={13} className="inline" /> Remove
              </button>
            )}
            <div className="text-xs text-slate-500 mt-2 max-w-sm">
              JPG or PNG. Max 10 MB. Landscape aspect (recommended 1600×400 or 3:1). Shows as the hero banner on your team's Dashboard.
            </div>
          </div>
        </div>
      </Section>

      {/* Colors */}
      <Section title="Brand Colors" icon={Palette}>
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <ColorField label="Primary" value={tenant.colors?.primary} onChange={(v) => update('colors.primary', v)} testid="color-primary" />
          <ColorField label="Accent" value={tenant.colors?.accent} onChange={(v) => update('colors.accent', v)} testid="color-accent" />
          <ColorField label="Highlight" value={tenant.colors?.highlight} onChange={(v) => update('colors.highlight', v)} testid="color-highlight" />
        </div>
        <div className="text-xs text-slate-500 mb-2 font-semibold uppercase tracking-wider">Presets</div>
        <div className="flex flex-wrap gap-2">
          {PALETTE_PRESETS.map((p) => (
            <button key={p.name}
                    onClick={() => { update('colors.primary', p.primary); update('colors.accent', p.accent); update('colors.highlight', p.highlight); }}
                    data-testid={`preset-${p.name.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
                    className="px-3 py-2 rounded-lg text-xs font-semibold border border-slate-200 hover:border-slate-400 inline-flex items-center gap-2">
              <span className="flex gap-0.5">
                <span className="w-3 h-3 rounded-sm" style={{ background: p.primary }} />
                <span className="w-3 h-3 rounded-sm" style={{ background: p.accent }} />
                <span className="w-3 h-3 rounded-sm" style={{ background: p.highlight }} />
              </span>
              {p.name}
            </button>
          ))}
        </div>
      </Section>

      {/* Hero copy */}
      <Section title="Hero" icon={Sparkles}>
        <div className="space-y-3">
          <TextField label="Headline" value={tenant.content?.hero_headline} onChange={(v) => update('content.hero_headline', v)} testid="hero-headline" />
          <TextField label="Sub-headline" value={tenant.content?.hero_subhead} onChange={(v) => update('content.hero_subhead', v)} multiline testid="hero-subhead" />
          <div className="grid md:grid-cols-2 gap-3">
            <TextField label="Primary CTA text" value={tenant.content?.hero_cta_primary} onChange={(v) => update('content.hero_cta_primary', v)} testid="hero-cta-primary" />
            <TextField label="Secondary CTA text" value={tenant.content?.hero_cta_secondary} onChange={(v) => update('content.hero_cta_secondary', v)} testid="hero-cta-secondary" />
          </div>
        </div>
      </Section>

      {/* Features */}
      <Section title={`Features (up to ${MAX_FEATURES})`} icon={Building2}>
        <RepeaterList
          items={tenant.content?.features || []}
          onChange={(items) => update('content.features', items)}
          fields={[
            { key: 'title', label: 'Title', placeholder: 'Fast pre-approvals' },
            { key: 'body', label: 'Body', placeholder: 'Get approved in 24 hours...' },
            { key: 'icon', label: 'Icon', options: ICON_OPTIONS },
          ]}
          max={MAX_FEATURES}
          testidPrefix="feature"
        />
      </Section>

      {/* Testimonials */}
      <Section title="Testimonials" icon={Sparkles}>
        <RepeaterList
          items={tenant.content?.testimonials || []}
          onChange={(items) => update('content.testimonials', items)}
          fields={[
            { key: 'name', label: 'Name', placeholder: 'Sarah Chen' },
            { key: 'role', label: 'Role', placeholder: 'First-time homebuyer' },
            { key: 'initials', label: 'Initials', placeholder: 'SC' },
            { key: 'quote', label: 'Quote', placeholder: 'They closed in 21 days...' },
          ]}
          max={6}
          testidPrefix="testimonial"
        />
      </Section>

      {/* Contact */}
      <Section title="Contact" icon={Building2}>
        <div className="grid md:grid-cols-3 gap-3">
          <TextField label="Phone" value={tenant.content?.contact_phone} onChange={(v) => update('content.contact_phone', v)} testid="contact-phone" kind="phone" />
          <TextField label="Email" value={tenant.content?.contact_email} onChange={(v) => update('content.contact_email', v)} testid="contact-email" />
          <TextField label="Address" value={tenant.content?.contact_address} onChange={(v) => update('content.contact_address', v)} testid="contact-address" />
        </div>
        <div className="grid md:grid-cols-3 gap-3 mt-3">
          <TextField label="Contact form" value={tenant.content?.lead_form_slug} defaultLabel="No form"
                     onChange={(v) => update('content.lead_form_slug', v)} testid="lead-form-slug"
                     options={leadForms.map((f) => [f.slug, f.name])} />
        </div>
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <TextField label="Footer tagline" value={tenant.content?.footer_tagline} onChange={(v) => update('content.footer_tagline', v)} testid="footer-tagline" />
          <TextField label="NMLS # (mortgage only)" value={tenant.content?.nmls} onChange={(v) => update('content.nmls', v)} testid="nmls" />
        </div>
      </Section>

      {/* Sticky save bar */}
      <div className="sticky bottom-4 bg-white rounded-xl shadow-lg border border-slate-200 p-3 flex items-center justify-between">
        <div className="text-xs text-slate-500">
          Changes save when you click <b>Save</b>. Publish toggle above to make live.
        </div>
        <button onClick={save} disabled={busy} data-testid="save-brand-btn-bottom"
                className="px-4 py-2 rounded-lg bg-[#070B5F] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2">
          <Save size={14} /> {busy ? 'Saving…' : 'Save changes'}
        </button>
      </div>
        </div>

        {/* Right column — live preview */}
        <LivePreviewPanel tenant={tenant} />
      </div>
    </div>
  );
}


// ============================================================================
// Small helpers
// ============================================================================
function Section({ title, icon: Ic, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        {Ic && <Ic size={16} style={{ color: '#6BA82E' }} />}
        <div className="font-serif font-semibold" style={{ color: '#070B5F' }}>{title}</div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

const DEFAULT_OPTION = '__default';

function TextField({ label, value, onChange, multiline, testid, kind, options, defaultLabel = 'Default' }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-1">{label}</label>
      {options
        ? (
          <Select value={value || DEFAULT_OPTION} onValueChange={(v) => onChange(v === DEFAULT_OPTION ? '' : v)}>
            <SelectTrigger data-testid={testid} className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={DEFAULT_OPTION}>{defaultLabel}</SelectItem>
              {options.map(([key, name]) => <SelectItem key={key} value={key}>{name}</SelectItem>)}
              {/* a key saved before this picker existed stays visible until it is changed */}
              {value && !options.some(([key]) => key === value) && <SelectItem value={value}>{value} (unknown)</SelectItem>}
            </SelectContent>
          </Select>
        )
        : kind === 'phone'
        ? <PhoneInput value={value || ''} onChange={onChange} data-testid={testid} />
        : multiline
        ? <textarea rows={3} data-testid={testid} value={value || ''} onChange={(e) => onChange(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#070B5F]" />
        : <Input type="text" data-testid={testid} value={value || ''} onChange={(e) => onChange(e.target.value)} />}
    </div>
  );
}

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

function ColorField({ label, value, onChange, testid }) {
  const bad = !HEX_COLOR.test(value || '');
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" data-testid={testid} value={bad ? '#000000' : value} onChange={(e) => onChange(e.target.value)}
               className="w-12 h-10 rounded border border-slate-200 cursor-pointer" />
        <Input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} aria-invalid={bad}
               data-testid={`${testid}-text`}
               className={`flex-1 font-mono ${bad ? 'border-red-400 focus-visible:ring-red-500' : ''}`} />
      </div>
      {bad && <div className="text-xs text-red-600 mt-1" data-testid={`${testid}-error`}>Use a color like #1A2B3C</div>}
    </div>
  );
}

function RepeaterList({ items, onChange, fields, max, testidPrefix }) {
  const add = () => onChange([...items, Object.fromEntries(fields.map((f) => [f.key, '']))]);
  const remove = (i) => onChange(items.filter((_, x) => x !== i));
  const patch = (i, key, val) => onChange(items.map((it, x) => x === i ? { ...it, [key]: val } : it));

  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="p-3 rounded-lg border border-slate-100 bg-slate-50 relative" data-testid={`${testidPrefix}-item-${i}`}>
          <button onClick={() => remove(i)} className="absolute top-2 right-2 text-red-500 hover:bg-red-50 rounded p-1"
                  data-testid={`${testidPrefix}-remove-${i}`}>
            <Trash2 size={12} />
          </button>
          <div className="grid md:grid-cols-2 gap-2">
            {fields.map((f) => (
              <TextField
                key={f.key}
                label={f.label}
                value={it[f.key]}
                onChange={(v) => patch(i, f.key, v)}
                multiline={f.key === 'body' || f.key === 'quote'}
                options={f.options}
                testid={`${testidPrefix}-${i}-${f.key}`}
              />
            ))}
          </div>
        </div>
      ))}
      {items.length < max && (
        <button onClick={add} data-testid={`${testidPrefix}-add`}
                className="w-full py-2 rounded-lg border border-dashed border-slate-300 text-sm text-slate-500 hover:border-slate-500 hover:text-slate-700 inline-flex items-center justify-center gap-2">
          <Plus size={14} /> Add {testidPrefix}
        </button>
      )}
    </div>
  );
}


// ============================================================================
// LivePreviewPanel — sticky right-column preview of the tenant's public site.
// Renders the tenant's own template component (Modern/Classic/Bold) inside a
// scaled scrollable frame so unsaved changes reflect instantly.
// ============================================================================
function LivePreviewPanel({ tenant }) {
  const [mode, setMode] = useState('desktop');   // 'desktop' | 'mobile'
  const [nonce, setNonce] = useState(0);         // force remount on Refresh
  const isMobile = mode === 'mobile';
  // Frame dimensions (unscaled): desktop = 1280×800, mobile = 375×667
  const frameW = isMobile ? 375 : 1280;
  const frameH = isMobile ? 667 : 800;
  const PANEL_W = isMobile ? 320 : 640;
  const scale = PANEL_W / frameW;
  const scaledH = frameH * scale;

  return (
    <div className="hidden xl:block" data-testid="live-preview-panel">
      <div className="sticky top-4">
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye size={14} style={{ color: '#6BA82E' }} />
              <div className="font-serif font-semibold text-sm" style={{ color: '#070B5F' }}>Live Preview</div>
              <span className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase" style={{ background: '#EEF2FF', color: '#070B5F' }}>
                {tenant.template}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setMode('desktop')} data-testid="preview-desktop"
                      className={`p-1.5 rounded ${mode === 'desktop' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
                      title="Desktop view">
                <Monitor size={13} />
              </button>
              <button onClick={() => setMode('mobile')} data-testid="preview-mobile"
                      className={`p-1.5 rounded ${mode === 'mobile' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
                      title="Mobile view">
                <Smartphone size={13} />
              </button>
              <button onClick={() => setNonce((n) => n + 1)} data-testid="preview-refresh"
                      className="p-1.5 rounded text-slate-500 hover:bg-slate-100" title="Refresh preview">
                <RefreshCw size={13} />
              </button>
            </div>
          </div>

          {/* Frame */}
          <div className="p-4 bg-slate-100 flex justify-center">
            <div
              className="relative bg-white shadow-lg overflow-hidden"
              style={{
                width: PANEL_W,
                height: scaledH,
                borderRadius: isMobile ? 24 : 8,
                border: isMobile ? '8px solid #1e293b' : '1px solid #cbd5e1',
              }}
            >
              <div
                key={nonce}
                style={{
                  width: frameW,
                  height: frameH,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                  overflow: 'auto',
                  pointerEvents: 'none',  // prevent accidental clicks in preview
                }}
              >
                <TenantSiteRenderer tenant={tenant} />
              </div>
            </div>
          </div>

          {/* Footer note */}
          <div className="px-4 py-2 border-t border-slate-100 text-[10px] text-slate-500 text-center">
            {tenant.published ? 'Reflects unsaved edits. Save & publish to make live.' : 'Draft mode — save & publish to make live.'}
          </div>
        </div>
      </div>
    </div>
  );
}
